import { useState } from "react";
import { Box, Button, Menu, MenuItem, Stack, Tooltip, Typography } from "@mui/material";
import {
  ContentCopy,
  FilterList,
  Insights,
  Percent,
  ViewWeek,
} from "@mui/icons-material";
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid-premium";

import { percentileGradient } from "../../Elements/PercentileCell";
import { REPORT_SCALE } from "../../../util/reportColors";

/**
 * Which population the chips rank against.
 *
 * `published` is what the roll-up shipped — the whole league-season, above its
 * playtime floor — so a number here means the same thing it means on the Blitz
 * player page. `view` re-ranks against the players passing the current filters,
 * which answers "best of the group I've narrowed to" instead. They're genuinely
 * different questions, so the footer always states which one is on show.
 *
 * "vs current filters" rather than "vs current view": search has never been part
 * of the population — typing a name mustn't move the chips — so the old label
 * overpromised. See `PopulationFilters` in `filters.ts`.
 */
export type PercentileMode = "published" | "view" | "off";

/**
 * Which efficiency metric fills the PPP column slots: what he actually produced,
 * what the shots he generated were worth, or the gap between them.
 *
 * A toggle rather than three column groups — they answer the same question of the
 * same chances, and showing all three would triple the width of already-wide
 * tables. NBA only; the prospect garden is served no xPPP columns at all, so the
 * control hides itself there rather than offering a choice that does nothing.
 */
export type EfficiencyMode = "ppp" | "eppp" | "diff";

export const EFFICIENCY_MODE_LABELS: Record<EfficiencyMode, string> = {
  ppp: "PPP (actual)",
  eppp: "xPPP (expected)",
  diff: "PPP − xPPP",
};

export const PERCENTILE_MODE_LABELS: Record<PercentileMode, string> = {
  published: "vs league",
  view: "vs current filters",
  off: "off",
};

/**
 * `ToolbarPropsOverrides` is a single global augmentation shared by every grid in
 * the app, so these have to be optional — the Explorer grid's toolbar doesn't
 * take them, and MUI type-checks each slot against the union of all of them.
 */
declare module "@mui/x-data-grid-premium" {
  interface ToolbarPropsOverrides {
    percentileMode?: PercentileMode;
    onPercentileModeChange?: (mode: PercentileMode) => void;
    onOpenFilters?: (anchor: HTMLElement) => void;
    filtersOpen?: boolean;
    filterCount?: number;
    percentileModes?: PercentileMode[];
    efficiencyMode?: EfficiencyMode;
    onEfficiencyModeChange?: (mode: EfficiencyMode) => void;
  }
}

interface PlayerStatsToolbarProps {
  percentileMode?: PercentileMode;
  onPercentileModeChange?: (mode: PercentileMode) => void;
  onOpenFilters?: (anchor: HTMLElement) => void;
  filtersOpen?: boolean;
  /** Filters actually narrowing the table — a row still being typed doesn't count. */
  filterCount?: number;
  /** Modes this view can offer. The per-player view leaves out "view", which
   *  would rank a player against his own seasons and mean nothing. */
  percentileModes?: PercentileMode[];
  efficiencyMode?: EfficiencyMode;
  /** Absent when the served sections carry no xPPP columns — i.e. the prospect
   *  garden — in which case no control is rendered. */
  onEfficiencyModeChange?: (mode: EfficiencyMode) => void;
}

export default function PlayerStatsToolbar({
  percentileMode = "published",
  onPercentileModeChange,
  onOpenFilters,
  filtersOpen,
  filterCount = 0,
  percentileModes = ["published", "view", "off"],
  efficiencyMode = "ppp",
  onEfficiencyModeChange,
}: PlayerStatsToolbarProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [effAnchor, setEffAnchor] = useState<HTMLElement | null>(null);
  const [copied, setCopied] = useState(false);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };

  return (
    <GridToolbarContainer sx={{ px: 1.25, py: 0.5, borderBottom: "1px solid #e6e8eb" }}>
      <GridToolbarColumnsButton slotProps={{ button: { startIcon: <ViewWeek /> } }} />
      {onOpenFilters && (
        <Button
          size="small"
          startIcon={<FilterList />}
          onClick={(e) => onOpenFilters(e.currentTarget)}
          // A filtered table looks exactly like an unfiltered one — the rows that
          // would tell you are the rows that aren't there. So the count is the
          // only signal, and the button carries it into the ink as well as the
          // badge rather than staying muted next to its neighbours.
          sx={{
            color: filtersOpen
              ? "primary.main"
              : filterCount
                ? "#17191c"
                : undefined,
            fontWeight: filterCount ? 700 : undefined,
          }}
        >
          Filters
          {filterCount > 0 && (
            <Box
              component="span"
              sx={{
                ml: 0.75,
                px: 0.6,
                minWidth: 16,
                height: 16,
                borderRadius: "8px",
                backgroundColor: "#cf0a2c",
                color: "#fff",
                fontSize: 10.5,
                fontWeight: 700,
                lineHeight: "16px",
                textAlign: "center",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {filterCount}
            </Box>
          )}
        </Button>
      )}
      <Button
        size="small"
        startIcon={<Percent />}
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        Percentiles
      </Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {percentileModes.map((mode) => (
          <MenuItem
            key={mode}
            selected={mode === percentileMode}
            onClick={() => {
              onPercentileModeChange?.(mode);
              setAnchor(null);
            }}
          >
            {PERCENTILE_MODE_LABELS[mode]}
          </MenuItem>
        ))}
      </Menu>
      {onEfficiencyModeChange && (
        <>
          <Button
            size="small"
            startIcon={<Insights />}
            onClick={(e) => setEffAnchor(e.currentTarget)}
            // Named for what's on screen rather than "Efficiency": the button is the
            // only place the table says which of the three a PPP column is holding,
            // and the group headers change with it.
            sx={{ color: efficiencyMode === "ppp" ? undefined : "#17191c",
                  fontWeight: efficiencyMode === "ppp" ? undefined : 700 }}
          >
            {efficiencyMode === "ppp" ? "PPP" : efficiencyMode === "eppp" ? "xPPP" : "PPP Δ"}
          </Button>
          <Menu
            anchorEl={effAnchor}
            open={!!effAnchor}
            onClose={() => setEffAnchor(null)}
          >
            {(Object.keys(EFFICIENCY_MODE_LABELS) as EfficiencyMode[]).map((mode) => (
              <MenuItem
                key={mode}
                selected={mode === efficiencyMode}
                onClick={() => {
                  onEfficiencyModeChange(mode);
                  setEffAnchor(null);
                }}
              >
                {EFFICIENCY_MODE_LABELS[mode]}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
      <GridToolbarExport />
      <Tooltip title={copied ? "Copied!" : "Copy link to this view"}>
        <Button size="small" startIcon={<ContentCopy />} onClick={copyLink}>
          {copied ? "Copied!" : "Share"}
        </Button>
      </Tooltip>

      <Box flexGrow={1} />

      {percentileMode !== "off" && (
        <Stack direction="row" alignItems="center" spacing={1} pr={1}>
          <Typography
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: "0.1em",
              color: "#8a8a8a",
            }}
          >
            PERCENTILE
          </Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: REPORT_SCALE.bad }}>
            0
          </Typography>
          <Box
            sx={{
              width: 112,
              height: 7,
              borderRadius: "2px",
              background: percentileGradient(),
            }}
          />
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: REPORT_SCALE.good }}>
            100
          </Typography>
        </Stack>
      )}
    </GridToolbarContainer>
  );
}
